import { useCallback, useState, useRef } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { hideResizeHandles } from '../components/PDFBuilder/PDFeditor/imageUtils';

// 🔧 DEBUG CONFIGURATION
const DEBUG_LARGE_MAPS = true; // FORCE HUGE MAPS TO SEE WHAT'S HAPPENING
const DEBUG_VERBOSE_LOGGING = true; // Turn on spam logging

export type ExportStatus = 'idle' | 'exporting' | 'error' | 'done';

interface UseClientPDFExportOptions {
  onProgress?: (progress: number) => void;
  onComplete?: (pdfBlob: Blob) => void;
  onError?: (error: Error) => void;
}

interface UseClientPDFExportReturn {
  exportPDF: (filename?: string) => Promise<void>;
  status: ExportStatus;
  progress: number;
  error: string | null;
}

// A4 dimensions in pixels at 96 DPI (web standard)
const A4_WIDTH_PX = 595;
const A4_HEIGHT_PX = 842;
const A4_MARGIN_PX = 54;
const A4_CONTENT_WIDTH_PX = A4_WIDTH_PX - (A4_MARGIN_PX * 2); // 487px
const A4_CONTENT_HEIGHT_PX = A4_HEIGHT_PX - (A4_MARGIN_PX * 2); // 734px

// High-DPI settings for professional output  
const HIGH_DPI_SCALE = Math.max(window.devicePixelRatio * 2, 4); // Minimum 4x scale for 300 DPI
const JPEG_QUALITY = 0.8; // Balance between quality and file size

/**
 * High-fidelity PDF export hook for PropertyReporter document editor
 * 
 * Features:
 * - 300 DPI output with 4x scale rendering for crisp text and images
 * - iOS-matched GPS overlay flattening with correct positioning  
 * - CORS-enabled asset loading for OpenStreetMap tiles and property images
 * - JPEG compression (0.8 quality) for optimal file size
 * - Precise A4 layout matching (595x842px) to prevent layout shifts
 * - Robust progress tracking (0-100) with per-page updates
 */
export const useClientPDFExport = (
  editorRootRef: React.RefObject<HTMLElement>,
  options?: UseClientPDFExportOptions
): UseClientPDFExportReturn => {
  const [status, setStatus] = useState<ExportStatus>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const isExportingRef = useRef(false);

  /**
   * Scroll element into view and ensure proper positioning
   */
  const scrollToElement = useCallback(async (element: HTMLElement): Promise<void> => {
    element.scrollIntoView({ behavior: 'auto', block: 'start' });
    
    // Wait for scroll to complete
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Ensure element is fully visible
    const rect = element.getBoundingClientRect();
    if (rect.top < 0) {
      window.scrollBy(0, rect.top - 50);
      await new Promise(resolve => setTimeout(resolve, 200));
    }
  }, []);

  /**
   * Robust asset loading for CORS-enabled images and OpenStreetMap tiles
   * Ensures all images are fully loaded and CORS-cleared before capture
   */
  const waitForAssetsLoaded = useCallback(async (container: HTMLElement): Promise<void> => {
    // Find all images including those in overlays
    const allImages = container.querySelectorAll('img');
    const mapTiles = container.querySelectorAll('img[alt="Map tile"], .MuiBox-root img[src*="openstreetmap"]');
    const propertyImages = container.querySelectorAll('.inline-image');
    
    // Also check for React Portal overlays in the root document
    const rootMapTiles = document.querySelectorAll('img[alt="Map tile"], img[src*="openstreetmap"]');
    //console.log(`🗺️ Found ${mapTiles.length} map tiles in container, ${rootMapTiles.length} in root document`);
    
    const totalAssets = allImages.length;
    if (totalAssets === 0) return;

    //console.log(`🔍 Loading ${totalAssets} assets (${propertyImages.length} property images, ${mapTiles.length} map tiles)...`);

    // Enhanced asset loading with CORS handling
    const assetPromises = Array.from(allImages).map((img, index) => {
      return new Promise<void>((resolve) => {
        const imageElement = img as HTMLImageElement;
        
        // Force CORS for all images
        if (!imageElement.crossOrigin) {
          imageElement.crossOrigin = 'anonymous';
        }

        // Check if already loaded
        if (imageElement.complete && imageElement.naturalHeight !== 0) {
          //console.log(`✅ Asset ${index + 1}/${totalAssets} already loaded:`, imageElement.src.substring(0, 50) + '...');
          resolve();
          return;
        }

        // Set up loading handlers with timeout
        const timeout = setTimeout(() => {
          console.warn(`⚠️ Asset ${index + 1}/${totalAssets} load timeout:`, imageElement.src.substring(0, 50) + '...');
          resolve(); // Continue even if asset fails to load
        }, 20000); // 20 second timeout for better reliability

        const onLoad = () => {
          clearTimeout(timeout);
          //console.log(`✅ Asset ${index + 1}/${totalAssets} loaded:`, imageElement.src.substring(0, 50) + '...');
          resolve();
        };

        const onError = () => {
          clearTimeout(timeout);
          console.warn(`❌ Asset ${index + 1}/${totalAssets} failed to load:`, imageElement.src.substring(0, 50) + '...');
          
          // For OpenStreetMap tiles, try to reload with cache-busting parameter
          if (imageElement.src.includes('tile.openstreetmap.org') || imageElement.src.includes('openstreetmap')) {
            //console.log('🔄 Retrying OpenStreetMap tile with cache-busting...');
            const originalSrc = imageElement.src;
            const retryUrl = originalSrc + (originalSrc.includes('?') ? '&' : '?') + 'retry=' + Date.now();
            imageElement.src = '';
            setTimeout(() => {
              imageElement.src = retryUrl;
              // Give it one more chance to load
              setTimeout(() => {
                resolve();
              }, 3000);
            }, 100);
          } else {
            resolve(); // Continue even if asset fails
          }
        };

        imageElement.addEventListener('load', onLoad, { once: true });
        imageElement.addEventListener('error', onError, { once: true });

        // Trigger reload if src is already set but not loaded
        if (imageElement.src && !imageElement.complete) {
          const originalSrc = imageElement.src;
          imageElement.src = '';
          setTimeout(() => {
            imageElement.src = originalSrc;
          }, 10);
        }
      });
    });

    await Promise.all(assetPromises);
    //console.log('🎉 All assets loaded and CORS-ready');
  }, []);

  /**
   * Enhanced page rendering with iOS overlay flattening and high-DPI capture
   */
  const renderPageToCanvas = useCallback(async (
    pageElement: HTMLElement, 
    pageIndex: number
  ): Promise<HTMLCanvasElement> => {
    //console.log(`📸 Rendering page ${pageIndex + 1} with ${HIGH_DPI_SCALE}x scale for 300 DPI output...`);

    // Ensure page is visible and positioned correctly
    await scrollToElement(pageElement);
    
    // Wait for all assets including map tiles to load
    await waitForAssetsLoaded(pageElement);
    
    // ✨ Additional wait to ensure overlays and their images are loaded
    //console.log('🔍 Checking for overlay images before capture...');
    const overlayImages = Array.from(document.querySelectorAll('img[src*="openstreetmap"], img[alt="Map tile"]'));
    //console.log(`🗺️ Found ${overlayImages.length} map tiles in document before capture`);
    
    // Give extra time for any pending map tile loads
    if (overlayImages.length > 0) {
      //console.log('⏳ Waiting extra 2 seconds for map tiles to fully load...');
      await new Promise(resolve => setTimeout(resolve, 2000));
    }

    // Get precise A4 dimensions to prevent layout shifts
    const rect = pageElement.getBoundingClientRect();
    /*console.log(`📐 Page ${pageIndex + 1} dimensions:`, { 
      width: rect.width, 
      height: rect.height,
      expectedWidth: A4_WIDTH_PX,
      expectedHeight: A4_HEIGHT_PX
    });*/

    // 🎯 PRE-PROCESS: Find overlays that are actually rendered (React Portal overlays)
    //console.log('🔧 Pre-loading overlay images to ensure they render in PDF...');
    
    // Look for overlays in the scroll container where React Portals are rendered
    const scrollContainer = document.querySelector('[data-scroll-container]');
    const allOverlays = scrollContainer ? 
      Array.from(scrollContainer.querySelectorAll('*'))
        .filter(el => {
          const element = el as HTMLElement;
          const style = window.getComputedStyle(element);
          
          // Check if it's absolutely positioned (regardless of how - inline style or CSS class)
          const isAbsolutelyPositioned = style.position === 'absolute' || style.position === 'fixed';
          
          // Check if it contains overlay characteristics
          const hasOverlayContent = element.classList.contains('image-metadata-overlay') ||
                                   element.querySelector('img[src*="openstreetmap"]') ||
                                   element.querySelector('img[alt="Map tile"]') ||
                                   element.querySelector('[src*="tile.openstreetmap"]') ||
                                   element.textContent?.includes('AM') || 
                                   element.textContent?.includes('PM') ||
                                   style.backgroundColor.includes('rgba(0, 0, 0, 0.85)');
          
          if (isAbsolutelyPositioned && hasOverlayContent) {
            /*console.log('✅ Found overlay element:', {
              tagName: element.tagName,
              className: element.className,
              hasMapTile: !!element.querySelector('img[src*="openstreetmap"], img[alt="Map tile"]'),
              position: style.position,
              backgroundColor: style.backgroundColor,
              textContent: element.textContent?.substring(0, 50)
            });*/
          }
          
          return isAbsolutelyPositioned && hasOverlayContent;
        }) as HTMLElement[]
      : [];

    //(`🗺️ Pre-processing ${allOverlays.length} overlays for image loading (found in scroll container)...`);
    
    // Debug: Log the scroll container and what we found
    /*console.log('🔍 Overlay detection debug:', {
      scrollContainer: !!scrollContainer,
      scrollContainerChildren: scrollContainer?.children.length || 0,
      allElementsInContainer: scrollContainer ? Array.from(scrollContainer.querySelectorAll('*')).length : 0,
      absolutePositionedElements: scrollContainer ? Array.from(scrollContainer.querySelectorAll('*')).filter(el => {
        const style = window.getComputedStyle(el);
        return style.position === 'absolute' || style.position === 'fixed';
      }).length : 0,
      mapTilesInContainer: scrollContainer ? Array.from(scrollContainer.querySelectorAll('img[src*="openstreetmap"], img[alt="Map tile"]')).length : 0,
      foundOverlaysAfterFilter: allOverlays.length
    });*/
    
    // ✨ NEW: Filter overlays to only include those that intersect with THIS page
    const pageRect = pageElement.getBoundingClientRect();
    
    const pageOverlays = allOverlays.filter(overlay => {
      const overlayRect = overlay.getBoundingClientRect();
      
      // Check if overlay intersects with this page vertically
      const overlayBottom = overlayRect.top + overlayRect.height;
      const pageBottom = pageRect.top + pageRect.height;
      
      const intersects = !(overlayRect.top > pageBottom || overlayBottom < pageRect.top);
      
      // Check visibility state - only include visible overlays
      if (intersects) {
        const imageId = overlay.getAttribute('data-for-image') || overlay.getAttribute('data-overlay-id');
        
        if (imageId) {
          const img = document.querySelector(`img[data-image-id="${imageId}"]`) as HTMLImageElement;
          const isVisible = img?.getAttribute('data-metadata-visible') === 'true';
          
          if (!isVisible) {
            return false;
          }
        }
      }
      
      return intersects;
    });

    console.log(`\n>>> PDF EXPORT: Page ${pageIndex + 1} has ${pageOverlays.length} visible overlays <<<\n`);

    //console.log(`🗺️ Page ${pageIndex + 1}: Found ${pageOverlays.length} overlays out of ${allOverlays.length} total overlays`);
    
    // Pre-load all map images with CORS (only for this page's overlays)
    for (const overlay of pageOverlays) {
      const mapImages = Array.from(overlay.querySelectorAll('img[src*="openstreetmap"], img[alt="Map tile"]'));
      for (const img of mapImages) {
        const imgEl = img as HTMLImageElement;
        if (!imgEl.crossOrigin) {
          imgEl.crossOrigin = 'anonymous';
        }
        
        // Force reload if not complete
        if (!imgEl.complete || imgEl.naturalHeight === 0) {
          const originalSrc = imgEl.src;
          //console.log(`🔄 Pre-loading map tile: ${originalSrc.substring(0, 50)}...`);
          imgEl.src = '';
          imgEl.src = originalSrc;
        } else {
          //console.log(`✅ Map tile already loaded: ${imgEl.src.substring(0, 50)}...`);
        }
      }
    }
    
    // Wait for all map images to load
    await new Promise(resolve => setTimeout(resolve, 3000));
    //console.log('🎯 Pre-loading complete, starting overlay pre-injection...');

    // ✨ OPTION 1: PRE-INJECT overlays into the REAL page before html2canvas capture
    const tempOverlays: HTMLElement[] = [];
    const contentArea = pageElement.querySelector('.page-content');
    
    if (contentArea && pageOverlays.length > 0) {
      //console.log(`🔧 Pre-injecting ${pageOverlays.length} overlays into REAL page ${pageIndex + 1} before capture...`);
      
      for (let index = 0; index < pageOverlays.length; index++) {
        const overlay = pageOverlays[index];
        const overlayRect = overlay.getBoundingClientRect();
        
        // ✨ CRITICAL: Store original image URLs AND container dimensions + PARENT IMAGE SIZES
        const originalMapImages = Array.from(overlay.querySelectorAll('img[src*="openstreetmap"], img[src*="tile."], img[alt="Map tile"]'));
        const originalImageUrls = originalMapImages.map(img => (img as HTMLImageElement).src);
        
        // ✨ NEW: Get the ACTUAL INDIVIDUAL image dimensions for EACH overlay
        const parentImageDimensions = originalMapImages.map((img, imgIndex) => {
          const mapTile = img as HTMLImageElement;
          //console.log(`🔍 Searching for parent image for map tile ${imgIndex + 1}: ${mapTile.src?.substring(0, 50)}...`);
          
          let parentImg: HTMLImageElement | null = null;
          let actualRect: DOMRect | null = null;
          
          // Strategy 1: Look for closest .inline-image-container then find img.inline-image
          const container = mapTile.parentElement;
          let overlayScale = 1.0; // Default overlay scale
          
          if (container) {
            const imageContainer = container.closest('.inline-image-container');
            if (imageContainer) {
              parentImg = imageContainer.querySelector('img.inline-image') as HTMLImageElement;
              if (parentImg) {
                actualRect = parentImg.getBoundingClientRect(); // GET ACTUAL RENDERED RECT!
                
                // 🎯 CRITICAL: Read custom overlay scale from image's data attribute
                const scaleAttr = parentImg.getAttribute('data-overlay-scale');
                if (scaleAttr) {
                  overlayScale = parseFloat(scaleAttr);
                  //console.log(`🔍 Found custom overlay scale for image: ${overlayScale}x`);
                }
                
                //console.log(`✅ Strategy 1 success: Found parent via .inline-image-container -> img.inline-image`);
              }
            }
          }
          
          // Strategy 2: Search all images in the page content area for the one DIRECTLY UNDER this overlay
          if (!parentImg || !actualRect) {
            //console.log(`🔍 Strategy 1 failed, trying Strategy 2: finding image DIRECTLY under this overlay...`);
            // FIX: Search for images within the CURRENT page element, not the whole document
            const allImages = Array.from(pageElement.querySelectorAll('img')) as HTMLImageElement[];
            if (allImages.length > 0) {
              
              // Get page bounds for filtering
              const tempPageRect = pageElement.getBoundingClientRect();
              
              const propertyImages = allImages.filter(img => {
                // Filter out map tiles
                if (img.src.includes('openstreetmap') || img.src.includes('tile.')) {
                  return false;
                }
                
                // Must be reasonably large
                if (img.offsetWidth <= 50 || img.offsetHeight <= 50) {
                  return false;
                }
                
                // CRITICAL: Only include images that are visible on the current page
                const imgRect = img.getBoundingClientRect();
                
                // Image must be within the current page's Y bounds, relative to the viewport
                const isOnCurrentPage = !(imgRect.bottom < tempPageRect.top || imgRect.top > tempPageRect.bottom);
                
                if (!isOnCurrentPage && DEBUG_VERBOSE_LOGGING) {
                  //console.log(`   🚫 Excluding image at Y:${imgRect.top.toFixed(1)} (not on current page Y:${tempPageRect.top.toFixed(1)}-${tempPageRect.bottom.toFixed(1)})`);
                }
                
                return isOnCurrentPage;
              });
              
              //console.log(`🔍 Found ${propertyImages.length} images on current page (filtered from ${allImages.length} total)`);
              
              if (propertyImages.length > 0) {
                // Get the overlay's position
                const overlayRect = overlay.getBoundingClientRect();
                const overlayCenter = {
                  x: overlayRect.left + overlayRect.width / 2,
                  y: overlayRect.top + overlayRect.height / 2
                };
                
                //console.log(`🎯 Overlay ${imgIndex + 1} center: (${overlayCenter.x.toFixed(1)}, ${overlayCenter.y.toFixed(1)})`);
                
                let bestMatch: HTMLImageElement | null = null;
                let bestIoU = 0; // Best Intersection-over-Union score

                propertyImages.forEach((propImg, propImgIndex) => {
                  const imgRect = propImg.getBoundingClientRect();

                  // Calculate intersection area
                  const intersectionLeft = Math.max(overlayRect.left, imgRect.left);
                  const intersectionTop = Math.max(overlayRect.top, imgRect.top);
                  const intersectionRight = Math.min(overlayRect.right, imgRect.right);
                  const intersectionBottom = Math.min(overlayRect.bottom, imgRect.bottom);

                  const intersectionWidth = intersectionRight - intersectionLeft;
                  const intersectionHeight = intersectionBottom - intersectionTop;

                  let intersectionArea = 0;
                  if (intersectionWidth > 0 && intersectionHeight > 0) {
                    intersectionArea = intersectionWidth * intersectionHeight;
                  }

                  // Calculate union area
                  const overlayArea = overlayRect.width * overlayRect.height;
                  const imgArea = imgRect.width * imgRect.height;
                  const unionArea = overlayArea + imgArea - intersectionArea;

                  // Calculate Intersection-over-Union (IoU)
                  const iou = unionArea > 0 ? intersectionArea / unionArea : 0;

                  if (DEBUG_VERBOSE_LOGGING) {
                    //console.log(`   - Candidate ${propImgIndex + 1}: IoU = ${iou.toFixed(3)}`);
                  }

                  // Find the image with the highest IoU score
                  if (iou > bestIoU) {
                    bestIoU = iou;
                    bestMatch = propImg;
                  }
                });

                if (bestMatch && DEBUG_VERBOSE_LOGGING) {
                  //console.log(`   🏆 Best Match: Image with IoU = ${bestIoU.toFixed(3)}`);
                }
                
                if (bestMatch) {
                  parentImg = bestMatch;
                  actualRect = (bestMatch as HTMLImageElement).getBoundingClientRect();
                  
                  // 🎯 CRITICAL: Read custom overlay scale from the found image
                  const scaleAttr = (bestMatch as HTMLImageElement).getAttribute('data-overlay-scale');
                  if (scaleAttr) {
                    overlayScale = parseFloat(scaleAttr);
                    //console.log(`🔍 Found custom overlay scale from Strategy 2: ${overlayScale}x`);
                  }
                  
                  //console.log(`✅ Strategy 2 success: Found BEST MATCH image for overlay ${imgIndex + 1} (IoU: ${bestIoU.toFixed(3)})`);
                }
              }
            }
          }
          
          // Strategy 3: Use the largest property image in the entire document
          if (!parentImg || !actualRect) {
            //console.log(`🔍 Strategy 2 failed, trying Strategy 3: using largest property image...`);
            const allImages = Array.from(document.querySelectorAll('img')) as HTMLImageElement[];
            const propertyImages = allImages.filter(img => 
              !img.src.includes('openstreetmap') && 
              !img.src.includes('tile.') &&
              img.offsetWidth > 100 &&
              img.offsetHeight > 100
            );
            
            if (propertyImages.length > 0) {
              // Sort by size (area) and take the largest
              propertyImages.sort((a, b) => (b.offsetWidth * b.offsetHeight) - (a.offsetWidth * a.offsetHeight));
              parentImg = propertyImages[0];
              actualRect = (parentImg as HTMLImageElement).getBoundingClientRect(); // GET ACTUAL RENDERED RECT!
              
              // 🎯 CRITICAL: Read custom overlay scale from the fallback image
              const scaleAttr = (parentImg as HTMLImageElement).getAttribute('data-overlay-scale');
              if (scaleAttr) {
                overlayScale = parseFloat(scaleAttr);
                //console.log(`🔍 Found custom overlay scale from Strategy 3: ${overlayScale}x`);
              }
              
              //console.log(`✅ Strategy 3 success: Using largest property image as fallback`);
            }
          }
          
          if (parentImg && actualRect) {
            //console.log(`🖼️ FOUND INDIVIDUAL parent image for overlay ${imgIndex + 1}: ${actualRect.width}x${actualRect.height} (ACTUAL RENDERED SIZE), natural: ${parentImg.naturalWidth}x${parentImg.naturalHeight}, overlayScale: ${overlayScale}x, src: ${parentImg.src.substring(0, 50)}...`);
            return {
              // Use ACTUAL rendered size from getBoundingClientRect, not offsetWidth!
              offsetWidth: actualRect.width,  
              offsetHeight: actualRect.height,
              naturalWidth: parentImg.naturalWidth,
              naturalHeight: parentImg.naturalHeight,
              rect: actualRect, // Store the full rect for this specific overlay
              overlayScale: overlayScale // Store the custom overlay scale
            };
          }
          
          console.warn(`⚠️ ALL STRATEGIES FAILED: Could not find parent image for map tile, using fallback 200px (reasonable estimate)`);
          return { 
            offsetWidth: 200, 
            offsetHeight: 200, 
            naturalWidth: 200, 
            naturalHeight: 200,
            rect: null,
            overlayScale: 1.0 // Default scale
          };
        });
        
        // Store original map container dimensions (critical for proper sizing!)
        const originalMapContainers = originalMapImages.map(img => {
          const container = img.parentElement;
          if (container) {
            const containerStyle = window.getComputedStyle(container);
            return {
              width: containerStyle.width,
              height: containerStyle.height,
              borderRadius: containerStyle.borderRadius,
              overflow: containerStyle.overflow,
              flexShrink: containerStyle.flexShrink,
              position: containerStyle.position
            };
          }
          return null;
        });
        
        //console.log(`📋 Storing ${originalImageUrls.length} original image URLs for overlay ${index + 1}:`, originalImageUrls);
        //console.log(`📦 Storing ${originalMapContainers.length} original map container styles:`, originalMapContainers);
        //console.log(`🖼️ Storing ${parentImageDimensions.length} parent image dimensions:`, parentImageDimensions);
        
        // Clone the overlay
        const clonedOverlay = overlay.cloneNode(true) as HTMLElement;
        
        // Calculate position relative to this page's content area
        const pageRect = pageElement.getBoundingClientRect();
        const pageContentRect = contentArea.getBoundingClientRect();
        
        // ✅ FIX: Use consistent coordinate system - relative to content area only
        let relativeLeft = overlayRect.left - pageContentRect.left;
        let relativeTop = overlayRect.top - pageContentRect.top; // Remove A4_MARGIN_PX - it's causing double offset
        
        /*console.log(`🔧 Position calculation debug:
          overlayRect: {left: ${overlayRect.left}, top: ${overlayRect.top}}
          pageRect: {left: ${pageRect.left}, top: ${pageRect.top}}
          pageContentRect: {left: ${pageContentRect.left}, top: ${pageContentRect.top}}
          calculated: {left: ${relativeLeft}, top: ${relativeTop}}
        `);*/
        
        // Clamp to page bounds
        const maxY = A4_CONTENT_HEIGHT_PX - overlayRect.height;
        if (relativeTop > maxY) {
          relativeTop = Math.max(0, maxY);
        }
        
        // Style the pre-injected overlay
        clonedOverlay.style.position = 'absolute';
        clonedOverlay.style.left = `${relativeLeft}px`;
        clonedOverlay.style.top = `${relativeTop}px`;
        clonedOverlay.style.width = `${overlayRect.width}px`;
        clonedOverlay.style.height = `${overlayRect.height}px`;
        clonedOverlay.style.zIndex = '9999';
        clonedOverlay.style.pointerEvents = 'none';
        clonedOverlay.style.visibility = 'visible';
        clonedOverlay.style.opacity = '1';
        clonedOverlay.setAttribute('data-temp-overlay', `page-${pageIndex}-overlay-${index}`);

        // ⏳ WAIT FOR MAP TO FULLY LOAD BEFORE PROCESSING
        await new Promise(resolve => {
          const mapImages = clonedOverlay.querySelectorAll('img[src*="openstreetmap"], img[src*="tile."]');
          
          if (mapImages.length === 0) {
            resolve(true);
            return;
          }
          
          let loadedCount = 0;
          const totalMaps = mapImages.length;
          
          //console.log(`⏳ Waiting for ${totalMaps} map tiles to fully load...`);
          
          mapImages.forEach(img => {
            const mapImg = img as HTMLImageElement;
            
            const checkLoaded = () => {
              loadedCount++;
              //console.log(`✅ Map ${loadedCount}/${totalMaps} loaded: ${mapImg.src.substring(0, 50)}...`);
              
              if (loadedCount >= totalMaps) {
                //console.log(`🎉 ALL MAPS LOADED! Starting processing...`);
                setTimeout(resolve, 500); // Extra wait to be sure
              }
            };
            
            if (mapImg.complete && mapImg.naturalHeight > 0) {
              checkLoaded();
            } else {
              mapImg.onload = checkLoaded;
              mapImg.onerror = checkLoaded; // Don't block on errors
              
              // Force reload if needed
              if (mapImg.src) {
                const originalSrc = mapImg.src;
                mapImg.src = '';
                mapImg.src = originalSrc;
              }
            }
          });
        });

        // ✨ REPLICATE EXACT WORKING MAP PROCESSING FROM OLD SYSTEM
        const mapImages = clonedOverlay.querySelectorAll('img');
        const imageLoadPromises: Promise<void>[] = [];
        
        //console.log(`� Found ${mapImages.length} images in cloned overlay, had ${originalImageUrls.length} original URLs`);
        
        mapImages.forEach((img, imgIndex) => {
          const imgEl = img as HTMLImageElement;
          
          // ✨ RESTORE: Use original URL if available (CRITICAL FOR SUCCESS)
          const originalUrl = originalImageUrls[imgIndex];
          if (originalUrl && originalUrl !== imgEl.src) {
            //console.log(`🔄 Restoring original URL: ${originalUrl} (was: ${imgEl.src})`);
            imgEl.src = originalUrl;
          }
          
          // ✨ RESTORE: Original map container styling (CRITICAL FOR SIZING!)
          const originalContainerStyle = originalMapContainers[imgIndex];
          let containerWidth = 30; // fallback
          let containerHeight = 30; // fallback
          
          if (originalContainerStyle && imgEl.parentElement) {
            const container = imgEl.parentElement as HTMLElement;
            container.style.width = originalContainerStyle.width;
            container.style.height = originalContainerStyle.height;
            container.style.borderRadius = originalContainerStyle.borderRadius;
            container.style.overflow = originalContainerStyle.overflow;
            container.style.flexShrink = originalContainerStyle.flexShrink;
            container.style.position = originalContainerStyle.position;
            
            // Use proper container dimensions that match React overlay scaling  
            containerWidth = parseFloat(originalContainerStyle.width) || 40; // FIX: Remove 'let' to use outer scope
            containerHeight = parseFloat(originalContainerStyle.height) || 40; // FIX: Remove 'let' to use outer scope
            
            // 🔥 USE INDIVIDUAL PARENT IMAGE DIMENSIONS for THIS specific overlay
            const parentImageData = parentImageDimensions[imgIndex] || { offsetWidth: 200, offsetHeight: 200, overlayScale: 1.0 };
            const individualImageWidth = parentImageData.offsetWidth;  // ACTUAL RENDERED WIDTH for this specific image!
            const customOverlayScale = parentImageData.overlayScale || 1.0; // Get the custom overlay scale for THIS image
            
            //console.log(`🖼️ Using INDIVIDUAL parent image width for overlay ${imgIndex + 1}: ${individualImageWidth}px, customScale: ${customOverlayScale}x (each overlay scales independently!)`);
            
            // 🔥 INDIVIDUAL MetadataOverlay.tsx calculation using THIS overlay's actual image size AND custom scale
            const baseOverlayScale = 0.5; // Base scale from MetadataOverlay.tsx
            const effectiveOverlayScale = baseOverlayScale * customOverlayScale; // Apply user's custom scale multiplier
            const scaleFactor = individualImageWidth / 133; // From MetadataOverlay.tsx - USE THIS IMAGE'S ACTUAL WIDTH!  
            const mapSize = 40 * effectiveOverlayScale * scaleFactor; // Calculate based on THIS image's rendered size AND custom scale
            
            containerWidth = Math.round(mapSize);
            containerHeight = Math.round(mapSize);
            
            //console.log(`🎯 INDIVIDUAL SCALING for overlay ${imgIndex + 1}: imageWidth=${individualImageWidth}px, baseScale=${baseOverlayScale}, customScale=${customOverlayScale}x, effectiveScale=${effectiveOverlayScale}, scaleFactor=${scaleFactor.toFixed(2)}, mapSize=${mapSize.toFixed(1)}px`);
            
            // Force the container size in the DOM immediately
            container.style.width = `${containerWidth}px`;
            container.style.height = `${containerHeight}px`;
            container.style.minWidth = `${containerWidth}px`;
            container.style.minHeight = `${containerHeight}px`;
            container.style.maxWidth = `${containerWidth}px`;
            container.style.maxHeight = `${containerHeight}px`;
            
            // ✨ CRITICAL: Store container dimensions AFTER forcing to 300x300
            (imgEl as any).__containerWidth = containerWidth;
            (imgEl as any).__containerHeight = containerHeight;
            
            //console.log(`✅ Stored HUGE container dimensions: ${containerWidth}x${containerHeight}`);
          } else {
            // Fallback: Use stored parent image dimensions even when no container found
            const parentImageData = parentImageDimensions[imgIndex] || { offsetWidth: 200, offsetHeight: 200, overlayScale: 1.0 };
            const individualImageWidth = parentImageData.offsetWidth; // INDIVIDUAL width for this specific overlay
            const customOverlayScale = parentImageData.overlayScale || 1.0; // Get the custom overlay scale
            const fallbackBaseScale = 0.5; // Base scale from MetadataOverlay.tsx  
            const fallbackEffectiveScale = fallbackBaseScale * customOverlayScale; // Apply custom scale multiplier
            const fallbackScaleFactor = individualImageWidth / 133; // Use THIS image's actual rendered width
            const fallbackMapSize = 40 * fallbackEffectiveScale * fallbackScaleFactor; // Calculated for THIS specific image with custom scale
            
            containerWidth = Math.round(fallbackMapSize);
            containerHeight = Math.round(fallbackMapSize);
            (imgEl as any).__containerWidth = containerWidth;
            (imgEl as any).__containerHeight = containerHeight;
            //console.log(`✅ Fallback INDIVIDUAL scaling for overlay ${imgIndex + 1}: ${containerWidth}x${containerHeight}px (individualImageWidth=${individualImageWidth}px, customScale=${customOverlayScale}x, scaleFactor=${fallbackScaleFactor.toFixed(2)})`);
          }
          
          //console.log(`🗺️ Processing overlay image ${imgIndex + 1}: ${imgEl.src?.substring(0, 50)}...`);
          //console.log(`🔍 Full image src: ${imgEl.src}`);
          //console.log(`🔍 Image complete: ${imgEl.complete}, naturalHeight: ${imgEl.naturalHeight}`);
          
          // Create a promise for each image to ensure it loads (EXACTLY LIKE OLD SYSTEM)
          const imagePromise = new Promise<void>((resolve) => {
            // Ensure CORS for OpenStreetMap tiles
            imgEl.crossOrigin = 'anonymous';
            
            // Add image rendering optimization and FORCE FULL SIZE
            imgEl.style.imageRendering = '-webkit-optimize-contrast';
            imgEl.style.display = 'block';
            imgEl.style.objectFit = 'cover';
            imgEl.style.flexShrink = '0';
            imgEl.style.width = '100%';     // 🔥 FORCE FULL WIDTH
            imgEl.style.height = '100%';    // 🔥 FORCE FULL HEIGHT  
            imgEl.style.margin = '0';       // 🔥 NO MARGINS
            imgEl.style.padding = '0';      // 🔥 NO PADDING
            
            const onLoad = () => {
              //console.log(`✅ Map tile loaded in pre-injected overlay: ${imgEl.src?.substring(0, 50)}...`);
              
              // 🚨 PREVENT DOUBLE PROCESSING
              if ((imgEl as any).__processed) {
                //console.log(`⚠️ Map already processed, skipping: ${imgEl.src?.substring(0, 50)}...`);
                resolve();
                return;
              }
              (imgEl as any).__processed = true;
              
              // ✨ CRITICAL: Create canvas with stored container dimensions BEFORE image breaks
              const containerWidth = (imgEl as any).__containerWidth || 30;
              const containerHeight = (imgEl as any).__containerHeight || 30;
              
              // Don't process if container is too small
              if (containerWidth < 5 || containerHeight < 5) {
                console.warn(`⚠️ Container too small (${containerWidth}x${containerHeight}), skipping canvas processing`);
                resolve();
                return;
              }
              
              // Store original image data before canvas processing
              const originalImageSrc = imgEl.src;
              const originalNaturalWidth = imgEl.naturalWidth;
              const originalNaturalHeight = imgEl.naturalHeight;
              
              //console.log(`🎯 Processing map with stored dimensions: ${containerWidth}x${containerHeight}, image: ${originalNaturalWidth}x${originalNaturalHeight}`);
              
              // Create a new image element to avoid breaking the original
              const tempImage = new Image();
              tempImage.crossOrigin = 'anonymous';
              
              tempImage.onload = () => {
              //console.log(`🗺️ Map loaded: ${originalImageSrc.substring(0, 80)}...`);
              //console.log(`📐 Natural: ${tempImage.naturalWidth}x${tempImage.naturalHeight}`);
              
              // Verify this is actually a valid map tile
              if (tempImage.naturalWidth === 0 || tempImage.naturalHeight === 0) {
                console.warn(`❌ Invalid map tile dimensions, skipping processing`);
                resolve();
                return;
              }                // ✨ Now create the canvas with the corrected approach
                setTimeout(() => {
                  const canvas = document.createElement('canvas');
                  const ctx = canvas.getContext('2d');
                  if (ctx) {
                    // 🔥 HUGE CANVAS TO SEE THE ENTIRE MAP
                    canvas.width = containerWidth;   // Use the huge container size
                    canvas.height = containerHeight; // Use the huge container size
                    
                    // Clear canvas with white background first
                    ctx.fillStyle = 'white'; // White background for clean look
                    ctx.fillRect(0, 0, containerWidth, containerHeight);
                    
                    // ✨ DEBUG MODE: Show the ENTIRE map tile so we can see what's wrong!
                    const sourceImageWidth = tempImage.naturalWidth;
                    const sourceImageHeight = tempImage.naturalHeight;
                    
                    //console.log(`� HUGE MAP RENDERED: ${sourceImageWidth}x${sourceImageHeight} → ${containerWidth}x${containerHeight} with BIG PIN`);
                    
                    // Draw the map image to fill the container
                    ctx.drawImage(tempImage, 0, 0, containerWidth, containerHeight);
                    
                    // Add location pin with EXACT same scaling as MetadataOverlay.tsx
                    const centerX = containerWidth / 2;
                    const centerY = containerHeight / 2;
                    
                    // Calculate pin size using EXACT same scaling as MetadataOverlay.tsx
                    // From MetadataOverlay: width: `${6 * overlayScale * scaleFactor}px`
                    const scaleFactor = containerWidth / 40; // 40px is base map size from MetadataOverlay
                    const pinSize = 6 * scaleFactor; // 6px base size from MetadataOverlay.tsx  
                    const outerRadius = pinSize / 2; // Full white circle radius
                    const innerRadius = (pinSize * 2/3) / 2; // Inner red dot radius (slightly smaller)
                    
                    //console.log(`📍 UNIFIED PIN: container=${containerWidth}x${containerHeight}, scaleFactor=${scaleFactor.toFixed(2)}, pinSize=${pinSize.toFixed(1)}px, radii=${outerRadius.toFixed(1)}/${innerRadius.toFixed(1)}px`);
                    
                    // White outer circle (matches MetadataOverlay background: 'white')
                    ctx.fillStyle = 'white';
                    ctx.beginPath();
                    ctx.arc(centerX, centerY, outerRadius, 0, 2 * Math.PI);
                    ctx.fill();
                    
                    // Red inner dot (matches MetadataOverlay inner dot)
                    ctx.fillStyle = 'rgb(255, 68, 68)';
                    ctx.beginPath();
                    ctx.arc(centerX, centerY, innerRadius, 0, 2 * Math.PI);
                    ctx.fill();
                    
                    //console.log(`✅ UNIFIED SUCCESS: Created ${containerWidth}x${containerHeight} map with correctly scaled pin (${pinSize.toFixed(1)}px)`);
                  }
                  resolve();
                }, 10);
              };
              
              tempImage.onerror = () => {
                console.warn(`⚠️ Map tile failed to load: ${originalImageSrc}`);
                
                // Create fallback map when tile fails
                setTimeout(() => {
                  const canvas = document.createElement('canvas');
                  const ctx = canvas.getContext('2d');
                  if (ctx) {
                    canvas.width = containerWidth;
                    canvas.height = containerHeight;
                    
                    // Draw fallback map background
                    ctx.fillStyle = '#e6f3ff';
                    ctx.fillRect(0, 0, containerWidth, containerHeight);
                    
                    // Draw grid pattern to simulate map
                    ctx.strokeStyle = '#0066cc';
                    ctx.lineWidth = 1;
                    for (let i = 0; i < containerWidth; i += 10) {
                      ctx.beginPath();
                      ctx.moveTo(i, 0);
                      ctx.lineTo(i, containerHeight);
                      ctx.stroke();
                    }
                    for (let i = 0; i < containerHeight; i += 10) {
                      ctx.beginPath();
                      ctx.moveTo(0, i);
                      ctx.lineTo(containerWidth, i);
                      ctx.stroke();
                    }
                    
                    // Add location pin to fallback map (same scaling as successful maps)
                    const centerX = containerWidth / 2;
                    const centerY = containerHeight / 2;
                    const scaleFactor = containerWidth / 40; // 40px is base map size from MetadataOverlay
                    const pinSize = 6 * scaleFactor; // 6px base size from MetadataOverlay.tsx  
                    const outerRadius = pinSize / 2; // Full white circle radius
                    const innerRadius = (pinSize * 2/3) / 2; // Inner red dot radius
                    
                    // White outer circle
                    ctx.fillStyle = 'white';
                    ctx.beginPath();
                    ctx.arc(centerX, centerY, outerRadius, 0, 2 * Math.PI);
                    ctx.fill();
                    
                    // Red inner dot
                    ctx.fillStyle = 'rgb(255, 68, 68)';
                    ctx.beginPath();
                    ctx.arc(centerX, centerY, innerRadius, 0, 2 * Math.PI);
                    ctx.fill();
                    
                    // Replace with fallback map
                    imgEl.src = canvas.toDataURL('image/png');
                    //console.log(`✅ Created fallback map ${containerWidth}x${containerHeight} with correctly scaled pin (${pinSize.toFixed(1)}px)`);
                  }
                  resolve();
                }, 10);
              };
              
              // Load the temp image with the original source
              tempImage.src = originalImageSrc;
            };
            
            const onError = () => {
              console.warn(`⚠️ Map tile failed in pre-injected overlay, trying fallback: ${imgEl.src?.substring(0, 50)}...`);
              //console.log(`🔍 Failed image full URL: ${imgEl.src}`);
              
              // Check if this is actually an OpenStreetMap URL (EXACTLY LIKE OLD SYSTEM)
              if (imgEl.src && (imgEl.src.includes('openstreetmap') || imgEl.src.includes('tile.'))) {
                // Try different tile server as fallback
                const urlParts = imgEl.src.match(/\/(\d+)\/(\d+)\/(\d+)\.png/);
                if (urlParts) {
                  const [, z, x, y] = urlParts;
                  const fallbackUrl = `https://a.tile.openstreetmap.org/${z}/${x}/${y}.png`;
                  //console.log(`🔄 Trying fallback URL: ${fallbackUrl}`);
                  imgEl.src = fallbackUrl;
                  
                  // Give it one more chance
                  setTimeout(() => {
                    if (!imgEl.complete || imgEl.naturalHeight === 0) {
                      console.warn('⚠️ Fallback tile also failed, creating placeholder');
                      // Create a simple gray placeholder
                      imgEl.style.backgroundColor = '#ddd';
                      imgEl.style.border = '1px solid #ccc';
                      imgEl.alt = 'Map unavailable';
                    }
                    resolve();
                  }, 2000);
                } else {
                  console.warn('⚠️ Could not parse tile coordinates from URL, creating placeholder');
                  imgEl.style.backgroundColor = '#ddd';
                  imgEl.style.border = '1px solid #ccc';
                  imgEl.alt = 'Map unavailable';
                  resolve();
                }
              } else {
                console.warn('⚠️ Not an OpenStreetMap URL, checking if we can fix it');
                // Try to use the original URL if we have it (EXACTLY LIKE OLD SYSTEM)
                if (originalUrl && (originalUrl.includes('openstreetmap') || originalUrl.includes('tile.'))) {
                  //console.log(`🔄 Using stored original URL: ${originalUrl}`);
                  imgEl.src = originalUrl;
                  setTimeout(() => {
                    if (!imgEl.complete || imgEl.naturalHeight === 0) {
                      console.warn('⚠️ Original URL also failed, creating gray placeholder');
                      imgEl.style.backgroundColor = '#ddd';
                      imgEl.style.border = '1px solid #ccc';
                      imgEl.alt = 'Map unavailable';
                    }
                    resolve();
                  }, 2000);
                } else {
                  // Create a simple map-like placeholder with a pin icon
                  //console.log('🎨 Creating map-like placeholder');
                  imgEl.style.backgroundColor = '#e8f4f8';
                  imgEl.style.border = '1px solid #ccc';
                  imgEl.style.backgroundImage = 'radial-gradient(circle at center, #007AFF 2px, transparent 2px)';
                  imgEl.style.backgroundSize = '20px 20px';
                  imgEl.style.backgroundPosition = 'center';
                  imgEl.style.backgroundRepeat = 'no-repeat';
                  imgEl.alt = 'Map location';
                  resolve();
                }
              }
            };
            
            imgEl.addEventListener('load', onLoad, { once: true });
            imgEl.addEventListener('error', onError, { once: true });
            
            // Check if already loaded (EXACTLY LIKE OLD SYSTEM)
            if (imgEl.complete && imgEl.naturalHeight !== 0) {
              //console.log(`🎯 Image already loaded: ${imgEl.src?.substring(0, 50)}...`);
              onLoad();
            } else {
              // Check if src is valid before trying to reload
              let srcToUse = imgEl.src;
              
              // If current src is invalid, try to use the original URL
              if ((!srcToUse || (!srcToUse.includes('openstreetmap') && !srcToUse.includes('tile.'))) && originalUrl) {
                //console.log(`🔄 Using original URL instead of invalid src: ${originalUrl}`);
                srcToUse = originalUrl;
                imgEl.src = originalUrl;
              }
              
              if (srcToUse && (srcToUse.includes('openstreetmap') || srcToUse.includes('tile.'))) {
                //console.log(`🔄 Reloading valid tile URL: ${srcToUse}`);
                imgEl.src = '';
                setTimeout(() => {
                  imgEl.src = srcToUse;
                }, 10);
              } else {
                console.warn(`⚠️ No valid tile URL available, creating placeholder`);
                imgEl.style.backgroundColor = '#e8f4f8';
                imgEl.style.border = '1px solid #ccc';
                imgEl.style.backgroundImage = 'radial-gradient(circle at center, #007AFF 2px, transparent 2px)';
                imgEl.style.backgroundSize = '20px 20px';
                imgEl.style.backgroundPosition = 'center';
                imgEl.style.backgroundRepeat = 'no-repeat';
                imgEl.alt = 'Map location';
                resolve();
              }
            }
          });
          
          imageLoadPromises.push(imagePromise);
        });
        
        // Wait for all images in this overlay to load before continuing (CRITICAL!)
        await Promise.all(imageLoadPromises);
        
        // Read original computed styles for proper cloning
        const originalComputedStyle = window.getComputedStyle(overlay);
        
        // Read all text element styles to preserve them
        const originalTextStyles = Array.from(overlay.querySelectorAll('.MuiTypography-root')).map((el) => {
          const style = window.getComputedStyle(el);
          return {
            fontSize: style.fontSize,
            lineHeight: style.lineHeight,
            fontWeight: style.fontWeight,
            color: style.color
          };
        });
        clonedOverlay.style.display = 'flex'; // CRITICAL: must be flex
        clonedOverlay.style.flexDirection = 'row'; // CRITICAL: row layout (map + text side by side)
        clonedOverlay.style.alignItems = 'center'; // CRITICAL: vertically center map with text
        clonedOverlay.style.gap = originalComputedStyle.gap;
        clonedOverlay.style.padding = originalComputedStyle.padding;
        clonedOverlay.style.backgroundColor = originalComputedStyle.backgroundColor;
        clonedOverlay.style.borderRadius = originalComputedStyle.borderRadius;
        clonedOverlay.style.border = originalComputedStyle.border;
        clonedOverlay.style.fontFamily = originalComputedStyle.fontFamily;
        
        // Store reference to find it later
        (clonedOverlay as any).__overlayIndex = index + 1;
        
        const allElements = clonedOverlay.querySelectorAll('*');
        let textElementIndex = 0;
        
        allElements.forEach((element) => {
          const el = element as HTMLElement;
          
          // Fix text content container (MuiBox with Typography children)
          const hasMuiBoxClass = el.classList.contains('MuiBox-root');
          const hasMapImage = !!el.querySelector('img[src*="openstreetmap"]');
          const typographyCount = el.querySelectorAll('.MuiTypography-root').length;
          const isTextContentContainer = hasMuiBoxClass && !hasMapImage && typographyCount > 0;
          
          if (isTextContentContainer) {
            // Use cssText to forcefully override all existing styles
            const existingStyles = el.style.cssText;
            el.style.cssText = `
              display: flex !important;
              flex: 1 !important;
              min-width: 0 !important;
              flex-direction: column !important;
              justify-content: space-between !important;
              height: 100% !important;
              ${existingStyles}
            `;
            // Then re-apply critical ones to ensure they're last
            el.style.display = 'flex';
            el.style.flexDirection = 'column';
            el.style.justifyContent = 'space-between';
          }
          
          // Fix Typography text elements
          const tagName = el.tagName.toLowerCase();
          if (tagName === 'span' || tagName === 'p' || tagName.match(/^h[1-6]$/) || el.classList.contains('MuiTypography-root')) {
            const originalStyle = originalTextStyles[textElementIndex];
            
            if (originalStyle) {
              el.style.fontSize = originalStyle.fontSize;
              el.style.fontWeight = originalStyle.fontWeight;
              el.style.color = originalStyle.color;
              el.style.lineHeight = originalStyle.lineHeight;
            }
            
            el.style.overflow = 'hidden';
            el.style.textOverflow = 'ellipsis';
            el.style.whiteSpace = 'nowrap';
            el.style.display = 'block';
            el.style.maxWidth = '100%';
            el.style.boxSizing = 'border-box';
            el.style.margin = '0';
            el.style.padding = '0';
            
            if (el.classList.contains('MuiTypography-root')) {
              el.style.flex = '0 0 auto';
              el.style.minWidth = '0';
              textElementIndex++;
            }
          }
        });
        
        // Inject into real page FIRST so we can read computed styles
        contentArea.appendChild(clonedOverlay);
        tempOverlays.push(clonedOverlay);
        
        // Small delay to let browser calculate layout
        await new Promise(resolve => setTimeout(resolve, 50));
        
        // Re-apply flexbox styles AFTER DOM append because MUI emotion classes can override them
        // Note: Can't use :has() in querySelector, so find all MuiBox-root and filter manually
        const allBoxElements = clonedOverlay.querySelectorAll('.MuiBox-root');
        
        // Find the text container (has Typography children but no map image)
        let textContainerAfterAppend: HTMLElement | null = null;
        for (const box of Array.from(allBoxElements)) {
          const hasMapImage = box.querySelector('img[src*="openstreetmap"]');
          const typographyCount = box.querySelectorAll('.MuiTypography-root').length;
          
          if (!hasMapImage && typographyCount > 0) {
            textContainerAfterAppend = box as HTMLElement;
            break;
          }
        }
        
        // Force flex layout to ensure proper text distribution
        if (textContainerAfterAppend) {
          textContainerAfterAppend.style.setProperty('display', 'flex', 'important');
          textContainerAfterAppend.style.setProperty('flex-direction', 'column', 'important');
          textContainerAfterAppend.style.setProperty('justify-content', 'space-between', 'important');
          textContainerAfterAppend.style.setProperty('flex', '1', 'important');
          textContainerAfterAppend.style.setProperty('height', '100%', 'important');
        }
        
        //console.log(`✅ Pre-injected overlay ${index + 1} at (${relativeLeft.toFixed(1)}, ${relativeTop.toFixed(1)}) into real page`);
      }
      
      // Wait for all overlays to be processed before continuing
      //console.log(`🎯 All ${pageOverlays.length} overlays pre-injected and processed for page ${pageIndex + 1}`);
      
      // Small delay to let DOM settle and map pins render
      await new Promise(resolve => setTimeout(resolve, 1000)); // Longer delay for map processing
      //console.log(`✅ Pre-injection complete for page ${pageIndex + 1}, waiting for map processing...`);
    }

    //console.log('🎯 Starting html2canvas capture with overlays already in place...');

    try {
      // High-fidelity rendering with overlays already physically present in the page
      const canvas = await html2canvas(pageElement, {
      scale: HIGH_DPI_SCALE, // 4x scale for 300 DPI crisp output
      useCORS: true,
      allowTaint: false, // Strict CORS for better reliability
      backgroundColor: '#ffffff',
      width: A4_WIDTH_PX, // Precise A4 width to prevent layout shifts
      height: A4_HEIGHT_PX, // Precise A4 height to prevent layout shifts
      scrollX: 0,
      scrollY: 0,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      logging: true, // Enable logging to debug image issues
      imageTimeout: 30000, // Longer timeout for map tiles
      removeContainer: true,
      foreignObjectRendering: false, // Better text rendering at high DPI
      onclone: async (clonedDoc, clonedElement) => {
        //console.log('🔧 Flattening iOS overlays and optimizing for high-DPI export...');
        
        // Find the cloned page container
        const clonedPage = clonedDoc.querySelector('.page-container');
        if (!clonedPage) return;
        
        const pageEl = clonedPage as HTMLElement;
        
        // Apply precise A4 page styling for export
        pageEl.style.cssText = `
          width: ${A4_WIDTH_PX}px !important;
          height: ${A4_HEIGHT_PX}px !important;
          background-color: #ffffff !important;
          position: relative !important;
          overflow: visible !important;
          box-shadow: none !important;
          border: none !important;
          margin: 0 !important;
          padding: 0 !important;
          display: block !important;
          transform: none !important;
          font-smooth: always !important;
          -webkit-font-smoothing: antialiased !important;
          -moz-osx-font-smoothing: grayscale !important;
        `;

        // Optimize content area for high-DPI rendering
        const contentArea = clonedPage.querySelector('.page-content');
        if (contentArea) {
          const contentEl = contentArea as HTMLElement;
          const originalContentEl = pageElement.querySelector('.page-content') as HTMLElement;
          const originalComputedStyle = originalContentEl ? window.getComputedStyle(originalContentEl) : null;
          
          contentEl.style.cssText = `
            position: absolute !important;
            top: ${A4_MARGIN_PX}px !important;
            left: ${A4_MARGIN_PX}px !important;
            width: ${A4_CONTENT_WIDTH_PX}px !important;
            height: ${A4_CONTENT_HEIGHT_PX}px !important;
            overflow: visible !important;
            font-family: "Times New Roman", serif !important;
            font-size: 12px !important;
            line-height: 1.6 !important;
            color: #333333 !important;
            word-wrap: break-word !important;
            white-space: normal !important;
            box-sizing: border-box !important;
            font-smooth: always !important;
            -webkit-font-smoothing: antialiased !important;
            -moz-osx-font-smoothing: grayscale !important;
          `;
          
          // Preserve additional styles from the original if available
          if (originalComputedStyle) {
            contentEl.style.textAlign = originalComputedStyle.textAlign;
            //console.log(`📝 Preserved content area text alignment: ${originalComputedStyle.textAlign}`);
          }

          // ✨ CRITICAL: iOS Overlay Flattening System
          // Find React Portal overlays in the correct scroll container
          
          //console.log('🔍 Looking for overlays in scroll container...');
          
          // Find the scroll container where React Portals are rendered
          const scrollContainer = document.querySelector('[data-scroll-container]');
          if (!scrollContainer) {
            console.warn('⚠️ No scroll container found - overlays may not be detected');
          }
          
          // Look for metadata overlays in the scroll container using CSS classes
          const scrollOverlays = scrollContainer ? Array.from(scrollContainer.querySelectorAll('.image-metadata-overlay, [class*="metadata"], [class*="overlay"]')).filter(el => {
            const element = el as HTMLElement;
            const style = window.getComputedStyle(element);
            
            // Check if it's a positioned overlay with overlay characteristics
            return (style.position === 'absolute' || style.position === 'fixed') && 
                   (element.classList.contains('image-metadata-overlay') ||
                    element.querySelector('img[src*="openstreetmap"]') ||
                    element.querySelector('img[alt="Map tile"]') ||
                    element.querySelector('.metadata-map-tile') ||
                    (element.textContent?.includes('AM') || element.textContent?.includes('PM')));
          }) : [];
          
          //console.log(`📍 Found ${scrollOverlays.length} overlays in scroll container`);
          
          // Also check for any map tiles in the document for debugging
          const allMapTiles = Array.from(document.querySelectorAll('img[alt="Map tile"], img[src*="openstreetmap"], img[src*="tile.openstreetmap"]'));
          //console.log(`�️ Found ${allMapTiles.length} total map tiles in document`);
          
          // Use the pre-filtered page-specific overlays instead of complex search
          let rootOverlays: Element[] = pageOverlays;
          
          // No need for fallback search - we already have pre-filtered page-specific overlays
          //console.log(`� Using ${rootOverlays.length} pre-filtered overlays for page ${pageIndex + 1}`);
          
          // ⚡ DISABLED: Old overlay injection - using pre-injection instead
          //console.log(`🚫 Skipping old overlay injection for page ${pageIndex + 1} - overlays already pre-injected`);
          
          // Process overlays sequentially to handle async image loading properly
          for (let index = 0; index < 0; index++) { // DISABLED: set to 0
            const portalOverlay = rootOverlays[index];
            //console.log(`🔄 Cloning React Portal overlay ${index + 1}/${rootOverlays.length} into page ${pageIndex + 1}`);
            
            const originalOverlay = portalOverlay as HTMLElement;
            
            // ✨ CRITICAL: Store original image URLs before cloning
            const originalMapImages = Array.from(originalOverlay.querySelectorAll('img[src*="openstreetmap"], img[src*="tile."], img[alt="Map tile"]'));
            const originalImageUrls = originalMapImages.map(img => (img as HTMLImageElement).src);
            //console.log(`📋 Storing ${originalImageUrls.length} original image URLs:`, originalImageUrls);
            
            // Check if overlay is visible
            const computedStyle = window.getComputedStyle(originalOverlay);
            if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden' || computedStyle.opacity === '0') {
              console.warn(`⚠️ Overlay ${index + 1} is hidden, skipping`);
              continue;
            }
            
            const clonedOverlay = originalOverlay.cloneNode(true) as HTMLElement;
            const rect = originalOverlay.getBoundingClientRect();
            
            // Skip if overlay has no dimensions
            if (rect.width === 0 || rect.height === 0) {
              console.warn(`⚠️ Overlay ${index + 1} has no dimensions, skipping`);
              continue;
            }
            
            //console.log(`📐 Original overlay ${index + 1}: left=${computedStyle.left}, top=${computedStyle.top}, rect=${rect.left}x${rect.top}`);
            
            // Calculate proper positioning relative to the current page's content area
            const pageRect = pageElement.getBoundingClientRect();
            const pageContentRect = contentArea.getBoundingClientRect();
            
            // Fix for multi-page positioning: calculate position relative to the current page
            // For page 2+, we need to adjust for the page offset within the document
            let relativeLeft = rect.left - pageContentRect.left;
            let relativeTop = rect.top - pageRect.top - A4_MARGIN_PX; // Position relative to current page, not document
            
            // Clamp overlay position to stay within page bounds
            const maxY = A4_CONTENT_HEIGHT_PX - rect.height;
            if (relativeTop > maxY) {
              console.warn(`⚠️ Overlay ${index + 1} would be clipped (${relativeTop.toFixed(1)}px > ${maxY.toFixed(1)}px), adjusting to fit`);
              relativeTop = Math.max(0, maxY);
            }
            
            //console.log(`📍 Positioning overlay ${index + 1}:`);
            //console.log(`  Original overlay position: ${rect.left.toFixed(1)}, ${rect.top.toFixed(1)}`);
            //console.log(`  Page rect: ${pageRect.left.toFixed(1)}, ${pageRect.top.toFixed(1)}`);
            //console.log(`  Page content rect: ${pageContentRect.left.toFixed(1)}, ${pageContentRect.top.toFixed(1)}`);
            //console.log(`  Fixed relative position: ${relativeLeft.toFixed(1)}, ${relativeTop.toFixed(1)}`);
            //console.log(`  Page content bounds: 0-${A4_CONTENT_WIDTH_PX}px x 0-${A4_CONTENT_HEIGHT_PX}px`);
            
            // Preserve the exact original styling but adjust positioning for PDF capture
            // Keep all CSS classes and computed styles intact
            const originalComputedStyle = window.getComputedStyle(originalOverlay);
            
            // Copy all important styles that affect layout and appearance
            clonedOverlay.style.position = 'absolute';
            clonedOverlay.style.left = `${relativeLeft}px`;
            clonedOverlay.style.top = `${relativeTop}px`;
            clonedOverlay.style.width = originalComputedStyle.width;
            clonedOverlay.style.height = originalComputedStyle.height;
            clonedOverlay.style.visibility = 'visible';
            clonedOverlay.style.opacity = '1';
            clonedOverlay.style.pointerEvents = 'none';
            clonedOverlay.style.userSelect = 'none';
            clonedOverlay.style.zIndex = '9999'; // Ensure it's on top
            clonedOverlay.style.display = 'flex'; // Force display in case it's hidden
            
            // Preserve all layout and appearance properties from the original
            clonedOverlay.style.transform = originalComputedStyle.transform;
            clonedOverlay.style.transformOrigin = originalComputedStyle.transformOrigin;
            clonedOverlay.style.display = originalComputedStyle.display;
            clonedOverlay.style.flexDirection = originalComputedStyle.flexDirection;
            clonedOverlay.style.alignItems = originalComputedStyle.alignItems;
            clonedOverlay.style.gap = originalComputedStyle.gap;
            clonedOverlay.style.padding = originalComputedStyle.padding;
            clonedOverlay.style.backgroundColor = originalComputedStyle.backgroundColor;
            clonedOverlay.style.borderRadius = originalComputedStyle.borderRadius;
            clonedOverlay.style.boxShadow = originalComputedStyle.boxShadow;
            clonedOverlay.style.border = originalComputedStyle.border;
            clonedOverlay.style.fontFamily = originalComputedStyle.fontFamily;
            clonedOverlay.style.fontSize = originalComputedStyle.fontSize;
            clonedOverlay.style.fontWeight = originalComputedStyle.fontWeight;
            clonedOverlay.style.color = originalComputedStyle.color;
            clonedOverlay.style.lineHeight = originalComputedStyle.lineHeight;
            
            // Don't override the CSS classes - they contain the proper iOS styling
            //console.log(`📝 Preserved overlay classes: "${clonedOverlay.className}"`);
            //console.log(`🎨 Copied computed styles: bg=${originalComputedStyle.backgroundColor}, font=${originalComputedStyle.fontSize}, gap=${originalComputedStyle.gap}`);
            
            // Ensure CORS and proper loading for all images in overlay
            const mapImages = clonedOverlay.querySelectorAll('img');
            const imageLoadPromises: Promise<void>[] = [];
            
            //console.log(`🔍 Found ${mapImages.length} images in cloned overlay, had ${originalImageUrls.length} original URLs`);
            
            mapImages.forEach((img, imgIndex) => {
              const imgEl = img as HTMLImageElement;
              
              // ✨ RESTORE: Use original URL if available
              const originalUrl = originalImageUrls[imgIndex];
              if (originalUrl && originalUrl !== imgEl.src) {
                //console.log(`🔄 Restoring original URL: ${originalUrl} (was: ${imgEl.src})`);
                imgEl.src = originalUrl;
              }
              
              //console.log(`🗺️ Processing overlay image ${imgIndex + 1}: ${imgEl.src?.substring(0, 50)}...`);
              //console.log(`🔍 Full image src: ${imgEl.src}`);
              //console.log(`🔍 Image complete: ${imgEl.complete}, naturalHeight: ${imgEl.naturalHeight}`);
              
              // Create a promise for each image to ensure it loads
              const imagePromise = new Promise<void>((resolve) => {
                // Ensure CORS for OpenStreetMap tiles
                imgEl.crossOrigin = 'anonymous';
                
              // Fix map positioning - center the map properly in container
              imgEl.style.imageRendering = '-webkit-optimize-contrast';
              imgEl.style.display = 'block';
              imgEl.style.width = '100%';
              imgEl.style.height = '100%';
              imgEl.style.objectFit = 'cover';
              imgEl.style.objectPosition = 'center center';
              imgEl.style.verticalAlign = 'middle';
              imgEl.style.margin = '0 auto';                const onLoad = () => {
                  //console.log(`✅ Map tile loaded in cloned overlay: ${imgEl.src?.substring(0, 50)}...`);
                  
                  // ✨ UNIFIED APPROACH: Map + pin already processed in main section, no need for duplicate processing
                  //console.log(`✅ Map already processed with pin in unified approach, skipping duplicate processing`);
                  resolve();
                };
                
                const onError = () => {
                  console.warn(`⚠️ Map tile failed in cloned overlay, trying fallback: ${imgEl.src?.substring(0, 50)}...`);
                  //console.log(`🔍 Failed image full URL: ${imgEl.src}`);
                  
                  // Check if this is actually an OpenStreetMap URL
                  if (imgEl.src && (imgEl.src.includes('openstreetmap') || imgEl.src.includes('tile.'))) {
                    // Try different tile server as fallback
                    const urlParts = imgEl.src.match(/\/(\d+)\/(\d+)\/(\d+)\.png/);
                    if (urlParts) {
                      const [, z, x, y] = urlParts;
                      const fallbackUrl = `https://a.tile.openstreetmap.org/${z}/${x}/${y}.png`;
                      //console.log(`🔄 Trying fallback URL: ${fallbackUrl}`);
                      imgEl.src = fallbackUrl;
                      
                      // Give it one more chance
                      setTimeout(() => {
                        if (!imgEl.complete || imgEl.naturalHeight === 0) {
                          console.warn('⚠️ Fallback tile also failed, creating placeholder');
                          // Create a simple gray placeholder
                          imgEl.style.backgroundColor = '#ddd';
                          imgEl.style.border = '1px solid #ccc';
                          imgEl.alt = 'Map unavailable';
                        }
                        resolve();
                      }, 2000);
                    } else {
                      console.warn('⚠️ Could not parse tile coordinates from URL, creating placeholder');
                      imgEl.style.backgroundColor = '#ddd';
                      imgEl.style.border = '1px solid #ccc';
                      imgEl.alt = 'Map unavailable';
                      resolve();
                    }
                  } else {
                    console.warn('⚠️ Not an OpenStreetMap URL, checking if we can fix it');
                    // Try to use the original URL if we have it
                    if (originalUrl && (originalUrl.includes('openstreetmap') || originalUrl.includes('tile.'))) {
                      //console.log(`🔄 Using stored original URL: ${originalUrl}`);
                      imgEl.src = originalUrl;
                      setTimeout(() => {
                        if (!imgEl.complete || imgEl.naturalHeight === 0) {
                          console.warn('⚠️ Original URL also failed, creating gray placeholder');
                          imgEl.style.backgroundColor = '#ddd';
                          imgEl.style.border = '1px solid #ccc';
                          imgEl.alt = 'Map unavailable';
                        }
                        resolve();
                      }, 2000);
                    } else {
                      // Create a simple map-like placeholder with a pin icon
                      //console.log('🎨 Creating map-like placeholder');
                      imgEl.style.backgroundColor = '#e8f4f8';
                      imgEl.style.border = '1px solid #ccc';
                      imgEl.style.backgroundImage = 'radial-gradient(circle at center, #007AFF 2px, transparent 2px)';
                      imgEl.style.backgroundSize = '20px 20px';
                      imgEl.style.backgroundPosition = 'center';
                      imgEl.style.backgroundRepeat = 'no-repeat';
                      imgEl.alt = 'Map location';
                      resolve();
                    }
                  }
                };
                
                imgEl.addEventListener('load', onLoad, { once: true });
                imgEl.addEventListener('error', onError, { once: true });
                
                // Check if already loaded
                if (imgEl.complete && imgEl.naturalHeight !== 0) {
                  //console.log(`🎯 Image already loaded: ${imgEl.src?.substring(0, 50)}...`);
                  onLoad();
                } else {
                  // Check if src is valid before trying to reload
                  let srcToUse = imgEl.src;
                  
                  // If current src is invalid, try to use the original URL
                  if ((!srcToUse || (!srcToUse.includes('openstreetmap') && !srcToUse.includes('tile.'))) && originalUrl) {
                    //console.log(`🔄 Using original URL instead of invalid src: ${originalUrl}`);
                    srcToUse = originalUrl;
                    imgEl.src = originalUrl;
                  }
                  
                  if (srcToUse && (srcToUse.includes('openstreetmap') || srcToUse.includes('tile.'))) {
                    //console.log(`🔄 Reloading valid tile URL: ${srcToUse}`);
                    imgEl.src = '';
                    setTimeout(() => {
                      imgEl.src = srcToUse;
                    }, 10);
                  } else {
                    console.warn(`⚠️ No valid tile URL available, creating placeholder`);
                    imgEl.style.backgroundColor = '#e8f4f8';
                    imgEl.style.border = '1px solid #ccc';
                    imgEl.style.backgroundImage = 'radial-gradient(circle at center, #007AFF 2px, transparent 2px)';
                    imgEl.style.backgroundSize = '20px 20px';
                    imgEl.style.backgroundPosition = 'center';
                    imgEl.style.backgroundRepeat = 'no-repeat';
                    imgEl.alt = 'Map location';
                    resolve();
                  }
                }
              });
              
              imageLoadPromises.push(imagePromise);
            });
            
            // Wait for all images in this overlay to load before continuing
            await Promise.all(imageLoadPromises);
            //console.log(`✅ All ${mapImages.length} images in overlay ${index + 1} processed`);

            // Fix overlay container positioning and styling
            const allElementsInOverlay = clonedOverlay.querySelectorAll('*');
            allElementsInOverlay.forEach((element) => {
              const el = element as HTMLElement;
              const originalEl = originalOverlay.querySelector(`${el.tagName}:nth-of-type(${Array.from(el.parentNode?.children || []).indexOf(el) + 1})`) as HTMLElement;
              
              if (originalEl) {
                const originalStyle = window.getComputedStyle(originalEl);
                
                // Copy critical layout and appearance styles
                el.style.display = originalStyle.display;
                el.style.flexDirection = originalStyle.flexDirection;
                el.style.alignItems = originalStyle.alignItems;
                el.style.justifyContent = originalStyle.justifyContent;
                el.style.gap = originalStyle.gap;
                el.style.padding = originalStyle.padding;
                el.style.margin = originalStyle.margin;
                el.style.width = originalStyle.width;
                el.style.height = originalStyle.height;
                el.style.fontSize = originalStyle.fontSize;
                el.style.fontWeight = originalStyle.fontWeight;
                el.style.color = originalStyle.color;
                el.style.backgroundColor = originalStyle.backgroundColor;
                el.style.borderRadius = originalStyle.borderRadius;
                el.style.border = originalStyle.border;
                el.style.overflow = originalStyle.overflow;
                el.style.textOverflow = originalStyle.textOverflow;
                el.style.whiteSpace = originalStyle.whiteSpace;
                el.style.lineHeight = originalStyle.lineHeight;
                
                // 🎯 CRITICAL FIX: Preserve MetadataOverlay text truncation behavior (matches editor exactly)
                const tagName = el.tagName.toLowerCase();
                if (tagName === 'span' || tagName === 'p' || tagName.match(/^h[1-6]$/) || el.classList.contains('MuiTypography-root')) {
                  console.log(`🔍 DEBUG EXPORT: Processing text element:`, {
                    tagName,
                    hasTypographyClass: el.classList.contains('MuiTypography-root'),
                    textContent: el.textContent?.substring(0, 50),
                    originalWidth: el.style.width,
                    originalMaxWidth: el.style.maxWidth,
                    computedWidth: originalStyle.width
                  });
                  
                  // Copy the EXACT text truncation from MetadataOverlay.tsx
                  el.style.overflow = 'hidden';
                  el.style.textOverflow = 'ellipsis';
                  el.style.whiteSpace = 'nowrap';
                  el.style.display = 'block';
                  el.style.boxSizing = 'border-box';
                  
                  // Let the parent container width naturally constrain the text (like editor does)
                  // NO hard pixel limits - use the overlay's natural flex: 1 width
                  if (!el.style.maxWidth || el.style.maxWidth === '100%') {
                    el.style.maxWidth = '100%'; // Fill parent container
                  }
                  
                  // Ensure parent Typography has proper flex: 1 and minWidth: 0 for truncation
                  if (el.classList.contains('MuiTypography-root')) {
                    el.style.flex = '1';
                    el.style.minWidth = '0'; // Critical for flex truncation
                  }
                  
                  console.log(`✅ DEBUG EXPORT: Applied truncation styles:`, {
                    overflow: el.style.overflow,
                    textOverflow: el.style.textOverflow,
                    whiteSpace: el.style.whiteSpace,
                    maxWidth: el.style.maxWidth,
                    flex: el.style.flex,
                    minWidth: el.style.minWidth
                  });
                }
              }
              
              // Fix map container positioning specifically 
              if (el.querySelector('img[src*="openstreetmap"], img[alt="Map tile"]')) {
                //console.log('🔧 Fixing map container positioning - forcing center alignment');
                el.style.display = 'flex';
                el.style.alignItems = 'center';
                el.style.justifyContent = 'center';
                el.style.overflow = 'hidden';
                el.style.flexShrink = '0';
                el.style.alignSelf = 'center';
                
                // Also fix the parent overlay container alignment
                const parentOverlay = el.closest('.image-metadata-overlay, [class*="MuiBox-root"]');
                if (parentOverlay) {
                  const parentEl = parentOverlay as HTMLElement;
                  //console.log('🔧 Fixing parent overlay alignment');
                  parentEl.style.alignItems = 'center';
                  parentEl.style.gap = '6px'; // Ensure proper gap between map and text
                }
              }
              
              // 🎯 CRITICAL: Fix text content container (MuiBox with flex:1) to enable proper text truncation
              // This is the container that holds all the Typography elements (city, street, zip, timestamp)
              const isTextContentContainer = 
                el.classList.contains('MuiBox-root') && 
                !el.querySelector('img[src*="openstreetmap"]') && // Not the map container
                el.querySelectorAll('.MuiTypography-root').length > 0; // Contains Typography elements
              
              if (isTextContentContainer) {
                console.log(`🔍 DEBUG EXPORT: Found text content container:`, {
                  originalFlex: el.style.flex,
                  originalMinWidth: el.style.minWidth,
                  originalWidth: el.style.width,
                  computedFlex: originalEl ? window.getComputedStyle(originalEl).flex : 'N/A',
                  computedMinWidth: originalEl ? window.getComputedStyle(originalEl).minWidth : 'N/A',
                  typographyCount: el.querySelectorAll('.MuiTypography-root').length
                });
                
                el.style.flex = '1'; // Take remaining space after map
                el.style.minWidth = '0'; // CRITICAL for flex truncation to work
                el.style.display = 'flex';
                el.style.flexDirection = 'column';
                el.style.justifyContent = 'space-between';
                
                console.log(`✅ DEBUG EXPORT: Applied container flex styles:`, {
                  flex: el.style.flex,
                  minWidth: el.style.minWidth,
                  display: el.style.display,
                  flexDirection: el.style.flexDirection
                });
              }
              
              // Only ensure visibility and prevent layout issues
              if (el.style.display === 'none') {
                el.style.display = '';
              }
              if (el.style.visibility === 'hidden') {
                el.style.visibility = 'visible';
              }
              if (el.style.opacity === '0') {
                el.style.opacity = '1';
              }
            });
            
            // Append to the cloned page content area so html2canvas captures it
            if (contentArea) {
              try {
                contentArea.appendChild(clonedOverlay);
                //console.log(`✅ Injected overlay ${index + 1} at position (${relativeLeft.toFixed(1)}px, ${relativeTop.toFixed(1)}px) with size ${rect.width.toFixed(1)}x${rect.height.toFixed(1)}`);
                //console.log(`🎯 Overlay ${index + 1} final styles: position=${clonedOverlay.style.position}, z-index=${clonedOverlay.style.zIndex}, display=${clonedOverlay.style.display}, visibility=${clonedOverlay.style.visibility}`);
                
                // Debug: Check if overlay is within page bounds
                const isWithinBounds = relativeTop >= 0 && relativeTop <= A4_CONTENT_HEIGHT_PX && relativeLeft >= 0 && relativeLeft <= A4_CONTENT_WIDTH_PX;
                //console.log(`📏 Overlay ${index + 1} within page bounds: ${isWithinBounds} (Y: ${relativeTop.toFixed(1)} <= ${A4_CONTENT_HEIGHT_PX}, X: ${relativeLeft.toFixed(1)} <= ${A4_CONTENT_WIDTH_PX})`);
                
                // Double-check the overlay is actually in the DOM
                const injectedOverlay = contentArea.querySelector(`[data-overlay-id="overlay-${index}"]`);
                if (!injectedOverlay) {
                  clonedOverlay.setAttribute('data-overlay-id', `overlay-${index}`);
                }
              } catch (error) {
                console.error(`❌ Failed to inject overlay ${index + 1}:`, error);
              }
            } else {
              console.error(`❌ No content area found for overlay ${index + 1}`);
            }
          }          // Optimize all images for CORS and high-DPI
          const allImages = contentArea.querySelectorAll('img');
          allImages.forEach((img) => {
            const imgEl = img as HTMLImageElement;
            
            // Ensure CORS is enabled
            imgEl.crossOrigin = 'anonymous';
            
            // Preserve exact dimensions and positioning with high-DPI optimization
            const currentStyle = window.getComputedStyle(imgEl);
            imgEl.style.cssText += `
              max-width: 100% !important;
              height: auto !important;
              display: ${currentStyle.display} !important;
              vertical-align: ${currentStyle.verticalAlign} !important;
              font-smooth: always !important;
              -webkit-font-smoothing: antialiased !important;
              image-rendering: -webkit-optimize-contrast !important;
            `;
          });

          // Optimize text rendering and preserve alignment for high-DPI export
          const textElements = contentArea.querySelectorAll('p, h1, h2, h3, h4, h5, h6, span, div');
          textElements.forEach((element) => {
            const el = element as HTMLElement;
            const computedStyle = window.getComputedStyle(el);
            
            // Preserve text alignment from original element
            const textAlign = computedStyle.textAlign || el.style.textAlign;
            
            el.style.cssText += `
              font-smooth: always !important;
              -webkit-font-smoothing: antialiased !important;
              -moz-osx-font-smoothing: grayscale !important;
              text-align: ${textAlign} !important;
            `;
            
            //console.log(`📝 Preserving text alignment: ${textAlign} for element:`, el.tagName);
          });
        }
      }
    });

    /*console.log(`✅ Page ${pageIndex + 1} rendered to high-DPI canvas:`, {
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      scale: HIGH_DPI_SCALE,
      expectedWidth: A4_WIDTH_PX * HIGH_DPI_SCALE,
      expectedHeight: A4_HEIGHT_PX * HIGH_DPI_SCALE
    });*/

    // 🔍 Debug: Check if we captured any overlay content
    const ctx = canvas.getContext('2d');
    if (ctx) {
      // Sample some pixels from overlay areas to see if we captured them
      const imageData = ctx.getImageData(0, 0, 100, 100);
      const hasOverlayContent = Array.from(imageData.data).some((value, index) => {
        // Check for dark pixels (overlay background) every 4th value (alpha channel)
        return index % 4 === 3 && value > 0;
      });
      //console.log(`🎯 Canvas contains overlay content: ${hasOverlayContent}`);
    }

    return canvas;
    
    } finally {
      // ✨ CLEANUP: Remove temporary overlays from real page
      if (tempOverlays.length > 0) {
        //console.log(`🧹 Cleaning up ${tempOverlays.length} temporary overlays from page ${pageIndex + 1}...`);
        tempOverlays.forEach((overlay, index) => {
          try {
            if (overlay.parentNode) {
              overlay.parentNode.removeChild(overlay);
              //console.log(`✅ Removed temporary overlay ${index + 1}`);
            }
          } catch (error) {
            console.warn(`⚠️ Failed to remove temporary overlay ${index + 1}:`, error);
          }
        });
      }
    }
  }, [scrollToElement, waitForAssetsLoaded]);

  /**
   * Main export function - High-fidelity PDF generation with progress tracking
   */
  const exportPDF = useCallback(async (filename: string = 'PropertyReport.pdf'): Promise<void> => {
    if (isExportingRef.current) {
      console.warn('⚠️ Export already in progress');
      return;
    }

    isExportingRef.current = true;
    setStatus('exporting');
    setProgress(0);
    setError(null);

    // Immediately wait for React to settle and verify component is available
    await new Promise(resolve => setTimeout(resolve, 100));

    if (!editorRootRef.current) {
      throw new Error('Editor root element not available. Make sure the component is mounted.');
    }

    try {
      //console.log('🚀 Starting high-fidelity PDF export with iOS overlay flattening...');
      
      // Simple progress start
      setProgress(5);
      if (options?.onProgress) {
        options.onProgress(5);
      }
      
      // 🎯 CRITICAL: Deselect all images before export to prevent selection borders in PDF
      //console.log('🖼️ Deselecting all images to prevent blue borders in export...');
      hideResizeHandles();
      
      // Also clear any text selection that might interfere
      if (window.getSelection) {
        window.getSelection()?.removeAllRanges();
      }

      // Small progress update
      setProgress(10);
      if (options?.onProgress) {
        options.onProgress(10);
      }
      
      // Small delay to ensure deselection is complete
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Double-check that editorRootRef is still available before proceeding
      if (!editorRootRef.current) {
        throw new Error('Editor root element became unavailable during export preparation');
      }
      
      // Found pages
      const pageContainers = Array.from(
        editorRootRef.current.querySelectorAll('.page-container')
      ) as HTMLElement[];

      if (pageContainers.length === 0) {
        throw new Error('No page containers found. Make sure the document has content to export.');
      }

      //console.log(`📄 Found ${pageContainers.length} pages to export at ${HIGH_DPI_SCALE}x scale`);
      setProgress(15);
      if (options?.onProgress) {
        options.onProgress(15);
      }

      // Create high-quality PDF document with precise A4 dimensions
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: [A4_WIDTH_PX, A4_HEIGHT_PX], // Precise A4 dimensions
        compress: true,
        precision: 2
      });

      // Store original scroll position for restoration
      const originalScrollX = window.scrollX;
      const originalScrollY = window.scrollY;

      // Process each page with simple progress tracking
      for (let i = 0; i < pageContainers.length; i++) {
        const pageElement = pageContainers[i];
        
        //console.log(`🎯 Processing page ${i + 1}/${pageContainers.length}...`);
        
        // Calculate smooth progress from 15% to 85%
        const baseProgress = 15 + Math.round((i / pageContainers.length) * 70);
        const pageProgress = Math.round(70 / pageContainers.length);
        
        // Start page
        setProgress(baseProgress);
        if (options?.onProgress) {
          options.onProgress(baseProgress);
        }

        // Add new page for all pages except the first
        if (i > 0) {
          pdf.addPage([A4_WIDTH_PX, A4_HEIGHT_PX], 'portrait');
        }

        // Mid-page progress
        setProgress(baseProgress + Math.round(pageProgress * 0.3));
        if (options?.onProgress) {
          options.onProgress(baseProgress + Math.round(pageProgress * 0.3));
        }

        // Render page to high-DPI canvas with overlay flattening
        const canvas = await renderPageToCanvas(pageElement, i);
        
        // Almost done with page
        setProgress(baseProgress + Math.round(pageProgress * 0.7));
        if (options?.onProgress) {
          options.onProgress(baseProgress + Math.round(pageProgress * 0.7));
        }
        
        // Convert to JPEG with optimized compression for file size
        const imgData = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
        
        // Add image to PDF with precise dimensions
        pdf.addImage(imgData, 'JPEG', 0, 0, A4_WIDTH_PX, A4_HEIGHT_PX, undefined, 'FAST');
        
        // Page complete
        const endProgress = 15 + Math.round(((i + 1) / pageContainers.length) * 70);
        setProgress(endProgress);
        if (options?.onProgress) {
          options.onProgress(endProgress);
        }
        
        //console.log(`✅ Page ${i + 1}/${pageContainers.length} completed`);
      }

      // Restore original scroll position
      window.scrollTo(originalScrollX, originalScrollY);

      // Final processing
      setProgress(90);
      if (options?.onProgress) {
        options.onProgress(90);
      }

      //console.log('📦 Generating optimized PDF blob...');
      
      // Generate final PDF blob
      const pdfBlob = pdf.output('blob');
      
      // Almost complete
      setProgress(95);
      if (options?.onProgress) {
        options.onProgress(95);
      }
      
      // Update to 100% progress
      setProgress(100);
      if (options?.onProgress) {
        options.onProgress(100);
      }

      // Trigger automatic download
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      /*console.log(`🎉 High-fidelity PDF export completed successfully!`, {
        pages: pageContainers.length,
        scale: `${HIGH_DPI_SCALE}x`,
        quality: `${JPEG_QUALITY * 100}%`,
        size: `${(pdfBlob.size / 1024 / 1024).toFixed(2)} MB`,
        filename
      });*/

      setStatus('done');
      
      if (options?.onComplete) {
        options.onComplete(pdfBlob);
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown export error';
      console.error('❌ High-fidelity PDF export failed:', error);
      
      setStatus('error');
      setError(errorMessage);
      
      if (options?.onError) {
        options.onError(error as Error);
      }
      
      throw error;
    } finally {
      isExportingRef.current = false;
    }
  }, [renderPageToCanvas, options]);

  return {
    exportPDF,
    status,
    progress,
    error
  };
};